import React from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader, Money, EmptyState } from "@/components/common";

export default function Cart() {
  const { items, removeFromCart, updateQty, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const checkout = () => {
    if (!user) return navigate("/login");
    navigate("/checkout", { state: { items } });
  };

  if (items.length === 0)
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 min-h-[60vh]">
        <EmptyState icon={ShoppingCart} title="Your cart is empty" subtitle="Browse courses and products to add them here." action={<Button className="viriks-navy text-white" onClick={() => navigate("/courses")}>Browse Courses</Button>} />
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 min-h-[60vh]" data-testid="cart-page">
      <PageHeader title="Shopping Cart" subtitle={`${items.length} item(s)`} />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {items.map((it) => (
            <div key={`${it.type}-${it.id}`} className="bg-white border border-border rounded-lg p-4 flex gap-4 items-center" data-testid={`cart-item-${it.id}`}>
              <img src={it.thumbnail} alt={it.title} className="h-16 w-24 object-cover rounded-md" />
              <div className="flex-1">
                <h4 className="font-semibold text-navy text-sm">{it.title}</h4>
                <span className="text-xs text-muted-foreground capitalize">{it.type}</span>
              </div>
              {it.type === "product" && (
                <input type="number" min={1} value={it.qty} onChange={(e) => updateQty(it.id, it.type, parseInt(e.target.value) || 1)} className="w-16 border border-border rounded px-2 py-1 text-sm" />
              )}
              <span className="font-heading font-bold text-navy"><Money amount={it.price * (it.qty || 1)} /></span>
              <button onClick={() => removeFromCart(it.id, it.type)} className="text-crimson p-2 hover:bg-crimson/10 rounded" data-testid={`remove-${it.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="bg-white border border-border rounded-lg p-6 h-fit">
          <h3 className="font-heading font-bold text-lg text-navy mb-4">Order Summary</h3>
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Subtotal</span><Money amount={total} /></div>
          <div className="flex justify-between font-heading font-black text-lg text-navy border-t border-border pt-3 mt-3"><span>Total</span><Money amount={total} /></div>
          <Button className="w-full mt-5 viriks-amber text-white font-semibold" onClick={checkout} data-testid="checkout-btn">Proceed to Checkout</Button>
        </div>
      </div>
    </div>
  );
}
